import React, { useEffect, useRef, InputHTMLAttributes, CSSProperties } from "react";
import cn from "classnames";
import style from "./voting-panel.module.css";
import { registerCursorTracker, formatTime, DefaultTimeDelimiter, type FormatTimeOptions } from "cleave-zen";

import { VotingSessionConfig } from "shared/datamodel/schemas/voting-session";
import StyledButton, { ButtonSize } from "frontend/ui-components/styled-button";
import { clamp } from "utils/math-utils";
import { mergeRefs } from "react-merge-refs";
import { RoundedToggle } from "frontend/ui-components/rounded-toggle/rounded-toggle";
import { useIsCIMReorderEnabled } from "utils/url-utils";

export type PartialVotingSessionConfig = Omit<VotingSessionConfig, "ownerId" | "start" | "hidden">;

const infoIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none">
    <path stroke="#113357" d="M7.278 3.889v1.444" />
    <path stroke="#113357" d="M7.278 3.889v1.444" />
    <path stroke="#113357" d="M7.278 6.777v4.334" />
    <path stroke="#113357" d="M7.278 6.777v4.334" />
    <path
      stroke="#113357"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M1 7.5a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Z"
    />
  </svg>
);

export function VotingCreateSession({ onStart }: { onStart: (info: PartialVotingSessionConfig) => void }) {
  const timeInputRef = useRef<HTMLInputElement>();
  const isCimReorderEnabled = useIsCIMReorderEnabled();

  function onClick(e: Event) {
    const target = e.currentTarget as HTMLElement;
    const form = target instanceof HTMLFormElement ? target : target.closest("form");
    if (!form) {
      console.warn("can't find form element");
    } else {
      form.requestSubmit(); // trigger this so form checks its own validity
    }
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const title = form.title_.value;
    const votesPerUser = parseInt(form.votesPerUser.value) || 1;
    const private_ = form.private.checked;
    const oneVotePerObject = form.oneVotePerObject.checked;
    const [minutes = 0, seconds = 0] = form.duration.value.split(":").map((x: string) => parseInt(x) || 0);
    const session: PartialVotingSessionConfig = {
      title,
      duration: minutes * 60 + seconds,
      numVotes: votesPerUser,
      anonymous: private_,
      maxVotesOnObject: oneVotePerObject ? 1 : Number.MAX_SAFE_INTEGER,
    };
    onStart(session);
  }

  return (
    <>
      <form
        className={cn(style.basicContainer, style.startSession, isCimReorderEnabled ? style.cimPadded : style.padded)}
        onSubmit={handleSubmit}
      >
        <ol className={style.createFormList} style={isCimReorderEnabled ? { gap: "0.5em" } : undefined}>
          <li>
            <div className={style["list-header"]}>
              {!isCimReorderEnabled && <span className={style["number"]}>1.</span>}
              <span className={style["text"]}>Title</span>
            </div>
            <div className={style["list-widget"]}>
              {isCimReorderEnabled ? (
                <input
                  key="title-input-cim"
                  className={style.cimTitleInputField}
                  type="text"
                  placeholder="Add title"
                  name="title_"
                  required
                />
              ) : (
                <input
                  key="title-input-old"
                  className={style.titleInputField}
                  type="text"
                  placeholder="what are you voting about?"
                  name="title_"
                  required
                />
              )}
            </div>
          </li>

          <li>
            <div className={style["list-header"]}>
              {!isCimReorderEnabled && <span className={style["number"]}>2.</span>}
              <span className={style["text"]}>Votes per person</span>
            </div>
            <div className={style["list-widget"]}>
              <WithIncAndDecButtons
                onClick={(delta) => {
                  const input = document.querySelector("input[name=votesPerUser]") as HTMLInputElement;
                  input.value = Math.max(1, parseInt(input.value) + delta).toString();
                }}
              >
                <input
                  className={style.votingInput}
                  name="votesPerUser"
                  type="number"
                  required
                  min={1}
                  step={1}
                  defaultValue={1}
                />
              </WithIncAndDecButtons>
            </div>
          </li>

          <li className={isCimReorderEnabled ? style.cimListWidget : ""}>
            <div className={style["list-header"]}>
              {!isCimReorderEnabled && <span className={style["number"]}>3.</span>}
              <RoundedToggle
                name="private"
                design={isCimReorderEnabled ? "monday" : "default"}
                label={
                  <span style={{ display: "grid", placeContent: "center", gridTemplate: "1fr/9fr 1fr", gap: "1em" }}>
                    Anonymous Voting
                    <span style={{ display: "grid", placeContent: "center" }}>
                      {infoIcon}
                      {/*eslint-disable-next-line react/no-unknown-property */}
                      <div className="tooltip" tip-position="bottom" style={{ "--_p-inline": "10px" } as CSSProperties}>
                        Voter names
                        <br />
                        won&apos;t be shown
                      </div>
                    </span>
                  </span>
                }
                className={isCimReorderEnabled ? style.cimPushToEnd : style.pushToEnd}
              />
            </div>
          </li>

          <li className={isCimReorderEnabled ? style.cimListWidget : ""}>
            <div className={style["list-header"]}>
              {!isCimReorderEnabled && <span className={style["number"]}>4.</span>}
              <RoundedToggle
                design={isCimReorderEnabled ? "monday" : "default"}
                name="oneVotePerObject"
                label="One Vote per object"
                className={isCimReorderEnabled ? style.cimPushToEnd : style.pushToEnd}
              />
            </div>
          </li>

          <li>
            <div className={style["list-header"]}>
              {!isCimReorderEnabled && <span className={style["number"]}>5.</span>}
              <span className={style["text"]}>Timer</span>
            </div>
            <div className={style["list-widget"]}>
              <WithIncAndDecButtons
                onClick={(delta) => {
                  const input = timeInputRef.current as HTMLInputElement;
                  if (!input) return;
                  const [minutes = 5, seconds = 0] = input.value.split(":").map((x: string) => parseInt(x) || 0);
                  const newMinutes = clamp(minutes + delta, 1, 59);
                  input.value = `${newMinutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
                }}
              >
                <FormattedTimeInput
                  ref={timeInputRef}
                  name="duration"
                  placeholder="05:00"
                  defaultValue={"05:00"}
                  formatTimeOptions={{ delimiter: ":", delimiterLazyShow: true, timePattern: ["m", "s"] }}
                />
              </WithIncAndDecButtons>
            </div>
          </li>
        </ol>
        {/* TODO: Adi asked to make this larger */}
        <StyledButton
          tabIndex={0}
          title="Start vote"
          customStyle={{
            alignSelf: "center",
            width: "137px",
            borderRadius: "6px",
            color: "#FFF",
            fontFamily: "Poppins",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "normal",
            letterSpacing: "0.124px",
            marginTop: "auto",
            height: "32px",
          }}
          onClick={onClick}
          size={ButtonSize.big}
        />
      </form>
    </>
  );
}

function WithIncAndDecButtons({ onClick, children }: { onClick?: (delta: number) => void; children: React.ReactNode }) {
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  return (
    <div className={cn(style.largeNumberInput, style.lightGreyBorder, isCimReorderEnabled && style.cimNumberInput)}>
      <button
        type="button"
        onClick={(e) => {
          onClick && onClick(-1);
          e.currentTarget.blur();
        }}
        className={isCimReorderEnabled ? style.cimNumberInputButton : ""}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="31" fill="none" viewBox="0 0 15 15">
          <path stroke="#123864" d="M1.6 7.6h12.2" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      <div className={cn(style.lightGreyBorder, isCimReorderEnabled && style.cimNumberInput)}>{children}</div>
      <button
        type="button"
        onClick={(e) => {
          onClick && onClick(+1);
          e.currentTarget.blur();
        }}
        className={isCimReorderEnabled ? style.cimNumberInputButton : ""}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="31" fill="none" viewBox="0 0 15 15">
          <path stroke="#123864" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7.7 1.5v12.2" />
          <path stroke="#123864" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M1.6 7.6h12.2" />
        </svg>
      </button>
    </div>
  );
}

interface FormattedTimeInputProps extends InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  formatTimeOptions: FormatTimeOptions;
}

const FormattedTimeInput = React.forwardRef((props: FormattedTimeInputProps, ref) => {
  const { value, onChange, formatTimeOptions, ...rest } = props;

  const internalRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    // registerCursorTracker itself returns an unregister destructor
    // function so you can place it here for hook component unmount
    if (internalRef.current) {
      return registerCursorTracker({ input: internalRef.current!, delimiter: ":" });
    }
  }, []);

  const newRef = mergeRefs([ref, internalRef]);

  return (
    <input
      ref={newRef}
      autoComplete="off"
      aria-label="Time input" // used by playwright
      className={style.votingInput}
      value={value}
      onChange={(e) => {
        const value = e.target.value;
        const newValue = formatTime(value, {
          delimiter: DefaultTimeDelimiter,
          delimiterLazyShow: true,
          timePattern: ["m", "s"],
        });
        e.target.value = newValue;
        props.onChange?.(e);
      }}
      {...rest}
    />
  );
});

FormattedTimeInput.displayName = "FormattedTimeInput";
